import { useMutation } from "@apollo/client";
import { toast } from "react-hot-toast";
import {
  ImportPimsSeriesDocument,
  OrganizationEventsDocument,
  OrganizationProjectsDocument,
  CreateEventDocument,
  SourceType,
  SyncEventTicketsDocument,
  EventDetailsDocument,
  PimsRessourcesQuery,
} from "../../../../../__generated__/graphql";
import { ImportMode, PimsEvent, PimsProject } from "../types";

export function useImportLogic() {
  const [importPimsSeries, { loading: importPimsSeriesLoading }] = useMutation(
    ImportPimsSeriesDocument
  );
  const [createEvent, { loading: createEventLoading }] =
    useMutation(CreateEventDocument);
  const [syncEventTickets, { loading: syncEventTicketsLoading }] = useMutation(
    SyncEventTicketsDocument
  );

  const handleEventTicketsImport = async (
    selectedSingleEvent: string,
    eventId: string
  ) => {
    if (!selectedSingleEvent) {
      toast.error("Veuillez sélectionner un évènement");
      return false;
    }

    if (!eventId) {
      toast.error("ID de l'évènement manquant");
      return false;
    }

    try {
      await syncEventTickets({
        variables: {
          syncEventTicketsInput: {
            eventId: eventId,
            sourceId: selectedSingleEvent,
            sourceType: SourceType.Pims,
          },
        },
        refetchQueries: [
          {
            query: EventDetailsDocument,
            variables: { eventId: eventId },
          },
        ],
      });

      toast.success("Billetterie synchronisée avec succès");
      return true;
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Une erreur est survenue lors de la synchronisation"
      );
      return false;
    }
  };

  const handleEventsImport = async (selectedEventsData: PimsEvent[]) => {
    if (!selectedEventsData || selectedEventsData.length === 0) {
      toast.error("Veuillez sélectionner au moins un évènement");
      return false;
    }

    try {
      // Create each event individually
      for (const event of selectedEventsData) {
        if (!event) continue;

        await createEvent({
          variables: {
            createEventInput: {
              name: event.label || "Évènement importé",
              startDate: event.datetime,
              sourceType: SourceType.Pims,
              sourceId: event.id,
            },
          },
          refetchQueries: [OrganizationEventsDocument],
        });
      }

      toast.success("Évènements importés avec succès");
      return true;
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Une erreur est survenue lors de l'importation des évènements"
      );
      return false;
    }
  };

  const handleProjectsImport = async (
    selectedProjectsData: PimsProject[],
    selectedEventsData: PimsEvent[]
  ) => {
    try {
      await importPimsSeries({
        variables: {
          importPimsSeriesInput: {
            series: selectedProjectsData?.map((project) => {
              if (!project) return null;
              return {
                id: project.id,
                label: project.label || "",
              };
            }),
            events: selectedEventsData?.map((event) => {
              if (!event) return null;
              return {
                id: event.id,
                label: event.label || "",
                venueId: event.venueId || "",
                datetime: event.datetime,
                maxCapacity: event.maxCapacity,
                costingCapacity: event.costingCapacity,
                serieId: event.serieId,
              };
            }),
          },
        },
        refetchQueries: [
          OrganizationProjectsDocument,
          OrganizationEventsDocument,
        ],
      });

      toast.success("Projets importés avec succès");
      return true;
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Une erreur est survenue lors de l'importation"
      );
      return false;
    }
  };

  const handleImport = async (
    mode: ImportMode,
    data: PimsRessourcesQuery | undefined,
    selectedProjects: Set<string>,
    selectedEvents: Set<string>,
    selectedSingleEvent: string,
    eventId?: string
  ) => {
    if (mode === "eventTickets") {
      return await handleEventTicketsImport(selectedSingleEvent, eventId!);
    } else if (mode === "events") {
      const selectedEventsData = data?.pimsRessources?.events?.filter(
        (event: PimsEvent | null) => {
          if (!event?.id) return false;
          return selectedEvents.has(event?.id);
        }
      );
      return await handleEventsImport(selectedEventsData as PimsEvent[]);
    } else {
      const selectedProjectsData = data?.pimsRessources?.series?.filter(
        (project: PimsProject | null) => {
          if (!project?.id) return false;
          return selectedProjects.has(project?.id);
        }
      );
      const selectedEventsData = data?.pimsRessources?.events?.filter(
        (event: PimsEvent | null) => {
          if (!event?.id) return false;
          return selectedEvents.has(event?.id);
        }
      );
      return await handleProjectsImport(
        (selectedProjectsData?.filter(Boolean) || []) as PimsProject[],
        (selectedEventsData?.filter(Boolean) || []) as PimsEvent[]
      );
    }
  };

  const isLoading =
    importPimsSeriesLoading || createEventLoading || syncEventTicketsLoading;

  return {
    handleImport,
    isLoading,
  };
}
